<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import { GalleryItemStatus, GalleryItemType, ExternalVideoMetadataStatus } from '@/types/galleries'
import type { OrganizerGalleryItem, UpdateItemPayload } from '@/types/galleries'
import type { ItemSaveResult } from '@/composables/useGallery'
import { formatBytes } from '@/utils/formatBytes'
import { itemDisplayName, itemPreviewImage } from '@/utils/galleries/imageSources'

const ALT_MAX = 300
const ALT_RECOMMENDED = 150
const CAPTION_MAX = 300
const CREDIT_MAX = 150

const props = defineProps<{
  open: boolean
  items: OrganizerGalleryItem[]
  itemId: string | null
  coverItemId: string | null
  /** Saves a sparse update and resolves with the server result (including 422 errors). */
  saveItem: (itemId: string, payload: UpdateItemPayload) => Promise<ItemSaveResult>
}>()

const emit = defineEmits<{
  close: []
  updated: [item: OrganizerGalleryItem]
  navigate: [itemId: string]
  delete: [itemId: string]
  'set-cover': [itemId: string]
  retry: [itemId: string]
}>()

const { t } = useI18n()

const form = reactive({ alt_text: '', caption: '', credit: '', is_hidden: false })
const saving = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const confirmDeleteOpen = ref(false)

const index = computed(() => props.items.findIndex((i) => i.id === props.itemId))
const item = computed<OrganizerGalleryItem | null>(() => props.items[index.value] ?? null)
const previous = computed(() => (index.value > 0 ? props.items[index.value - 1] ?? null : null))
const next = computed(() => (index.value >= 0 ? props.items[index.value + 1] ?? null : null))

const kindLabel = computed(() => {
  if (item.value?.type === GalleryItemType.Video) return t('galleries.item.kind_video')
  if (item.value?.type === GalleryItemType.ExternalVideo) return t('galleries.item.kind_link')
  return t('galleries.item.kind_photo')
})

const title = computed(() =>
  t('galleries.item.title_position', { kind: kindLabel.value, position: index.value + 1, total: props.items.length }),
)

watch(
  item,
  (current, before) => {
    if (current === null) return
    if (before?.id === current.id) return
    form.alt_text = current.alt_text ?? ''
    form.caption = current.caption ?? ''
    form.credit = current.credit ?? ''
    form.is_hidden = current.is_hidden
    serverErrors.value = {}
  },
  { immediate: true },
)

const dirtyPayload = computed<UpdateItemPayload>(() => {
  const current = item.value
  if (current === null) return {}
  const payload: UpdateItemPayload = {}
  const blank = (value: string): string | null => (value.trim() === '' ? null : value.trim())
  if (blank(form.alt_text) !== (current.alt_text ?? null)) payload.alt_text = blank(form.alt_text)
  if (blank(form.caption) !== (current.caption ?? null)) payload.caption = blank(form.caption)
  if (blank(form.credit) !== (current.credit ?? null)) payload.credit = blank(form.credit)
  if (form.is_hidden !== current.is_hidden) payload.is_hidden = form.is_hidden
  return payload
})

const isDirty = computed(() => Object.keys(dirtyPayload.value).length > 0)

const localErrors = computed<Record<string, string>>(() => {
  const errors: Record<string, string> = {}
  if (form.alt_text.length > ALT_MAX) errors['alt_text'] = t('galleries.form.too_long', { max: ALT_MAX })
  if (form.caption.length > CAPTION_MAX) errors['caption'] = t('galleries.form.too_long', { max: CAPTION_MAX })
  if (form.credit.length > CREDIT_MAX) errors['credit'] = t('galleries.form.too_long', { max: CREDIT_MAX })
  return errors
})

function errorFor(field: string): string | null {
  return localErrors.value[field] ?? serverErrors.value[field]?.[0] ?? null
}

const preview = computed(() => (item.value === null ? null : itemPreviewImage(item.value)))
const name = computed(() => (item.value === null ? '' : itemDisplayName(item.value)))
const dimensions = computed(() => {
  const current = item.value
  const size = current?.image ?? current?.video
  return size && size.width > 0 && size.height > 0 ? `${size.width} x ${size.height}` : null
})
const isFailed = computed(() => item.value?.status === GalleryItemStatus.Failed)
const isProcessing = computed(() => item.value?.status === GalleryItemStatus.Processing)
const metadataUnavailable = computed(
  () => item.value?.metadata_status === ExternalVideoMetadataStatus.Unavailable,
)
const canSetCover = computed(
  () => item.value !== null && preview.value !== null && props.coverItemId !== item.value.id,
)

/** Saves dirty fields. Returns false when the server or the form rejected them. */
async function save(): Promise<boolean> {
  const current = item.value
  if (current === null) return true
  if (Object.keys(localErrors.value).length > 0) return false
  if (!isDirty.value) return true

  saving.value = true
  serverErrors.value = {}
  try {
    const result = await props.saveItem(current.id, dirtyPayload.value)
    if (result.item === null) {
      serverErrors.value = result.errors
      return false
    }
    emit('updated', result.item)
    return true
  } finally {
    saving.value = false
  }
}

async function handleSave(): Promise<void> {
  if (await save()) emit('close')
}

async function goTo(target: OrganizerGalleryItem | null): Promise<void> {
  if (target === null || saving.value) return
  // Navigating saves dirty fields first and stays put when they are invalid.
  if (await save()) emit('navigate', target.id)
}

function handleClose(): void {
  if (saving.value) return
  emit('close')
}

function confirmDelete(): void {
  const current = item.value
  confirmDeleteOpen.value = false
  if (current !== null) emit('delete', current.id)
}
</script>

<template>
  <SlideOver :open="open && item !== null" :title="title" size="2xl" :saving="saving || confirmDeleteOpen" @close="handleClose">
    <div v-if="item !== null" class="space-y-5">
      <!-- Previous / next -->
      <div class="flex items-center justify-between">
        <button
          type="button"
          :disabled="previous === null || saving"
          class="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white disabled:opacity-40 disabled:hover:text-slate-300"
          @click="goTo(previous)"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('galleries.item.previous') }}
        </button>
        <button
          type="button"
          :disabled="next === null || saving"
          class="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white disabled:opacity-40 disabled:hover:text-slate-300"
          @click="goTo(next)"
        >
          {{ t('galleries.item.next') }}
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Preview -->
      <div class="rounded-xl bg-slate-900 overflow-hidden flex items-center justify-center min-h-48 max-h-96">
        <video
          v-if="item.video && item.status === GalleryItemStatus.Ready"
          class="max-h-96 w-full"
          controls
          preload="none"
          playsinline
          :src="item.video.url"
          :aria-label="name"
        >
          <track kind="captions" />
        </video>
        <GalleryPicture
          v-else-if="preview !== null"
          :image="preview"
          :alt="form.alt_text"
          variant="large"
          sizes="(min-width: 672px) 640px, 100vw"
          img-class="max-h-96 w-auto mx-auto object-contain"
        />
        <div v-else class="flex flex-col items-center gap-2 py-12 text-sm text-slate-500">
          <AppSpinner v-if="isProcessing" />
          <span>{{ isProcessing ? t('galleries.tile.processing') : t('galleries.item.no_preview') }}</span>
        </div>
      </div>

      <!-- File facts -->
      <dl class="grid grid-cols-3 gap-3 text-sm">
        <div class="col-span-3 sm:col-span-1">
          <dt class="text-xs text-slate-500">{{ t('galleries.item.file') }}</dt>
          <dd class="text-slate-200 truncate">{{ name || '-' }}</dd>
        </div>
        <div v-if="dimensions">
          <dt class="text-xs text-slate-500">{{ t('galleries.item.dimensions') }}</dt>
          <dd class="text-slate-200">{{ dimensions }}</dd>
        </div>
        <div v-if="item.bytes > 0">
          <dt class="text-xs text-slate-500">{{ t('galleries.item.size') }}</dt>
          <dd class="text-slate-200">{{ formatBytes(item.bytes) }}</dd>
        </div>
      </dl>

      <a
        v-if="item.external_video"
        :href="item.external_video.watch_url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-block text-sm text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
      >
        {{ t('galleries.item.open_original') }}
      </a>
      <p v-if="metadataUnavailable" class="text-sm text-amber-300">
        {{ t('galleries.item.metadata_unavailable') }}
        <button type="button" class="ml-1 underline underline-offset-2" @click="emit('retry', item.id)">
          {{ t('galleries.tile.retry') }}
        </button>
      </p>
      <p v-if="isFailed" class="text-sm text-red-300">{{ t('galleries.item.failed_hint') }}</p>

      <!-- Fields -->
      <form id="gallery-item-form" class="space-y-5" novalidate @submit.prevent="handleSave">
        <div>
          <label for="item-alt" class="block text-sm font-medium text-slate-300 mb-1.5">
            {{ t('galleries.item.alt') }}
          </label>
          <textarea
            id="item-alt"
            v-model="form.alt_text"
            rows="2"
            class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            :class="errorFor('alt_text') ? 'border-red-500' : 'border-slate-700'"
            aria-describedby="item-alt-hint"
          />
          <p v-if="errorFor('alt_text')" class="mt-1 text-xs text-red-400">{{ errorFor('alt_text') }}</p>
          <p id="item-alt-hint" class="mt-1 text-xs" :class="form.alt_text.length > ALT_RECOMMENDED ? 'text-amber-400' : 'text-slate-500'">
            {{ t('galleries.item.alt_hint', { recommended: ALT_RECOMMENDED }) }}
          </p>
        </div>

        <div>
          <label for="item-caption" class="block text-sm font-medium text-slate-300 mb-1.5">
            {{ t('galleries.item.caption') }}
          </label>
          <input
            id="item-caption"
            v-model="form.caption"
            type="text"
            class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            :class="errorFor('caption') ? 'border-red-500' : 'border-slate-700'"
          />
          <p v-if="errorFor('caption')" class="mt-1 text-xs text-red-400">{{ errorFor('caption') }}</p>
        </div>

        <div>
          <label for="item-credit" class="block text-sm font-medium text-slate-300 mb-1.5">
            {{ t('galleries.item.credit') }}
          </label>
          <input
            id="item-credit"
            v-model="form.credit"
            type="text"
            class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            :class="errorFor('credit') ? 'border-red-500' : 'border-slate-700'"
          />
          <p v-if="errorFor('credit')" class="mt-1 text-xs text-red-400">{{ errorFor('credit') }}</p>
        </div>

        <label class="flex items-start gap-3 cursor-pointer">
          <input v-model="form.is_hidden" type="checkbox" class="mt-0.5 accent-emerald-500" />
          <span>
            <span class="block text-sm text-slate-200">{{ t('galleries.item.hidden') }}</span>
            <span class="block text-xs text-slate-500">{{ t('galleries.item.hidden_hint') }}</span>
          </span>
        </label>

        <div>
          <p v-if="coverItemId === item.id" class="text-sm text-emerald-400">{{ t('galleries.item.is_cover') }}</p>
          <button
            v-else
            type="button"
            :disabled="!canSetCover"
            class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-40"
            @click="emit('set-cover', item.id)"
          >
            {{ t('galleries.item.set_cover') }}
          </button>
        </div>
      </form>
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-3">
        <button
          type="button"
          :disabled="saving"
          class="text-sm font-semibold text-red-400 hover:text-red-300 disabled:opacity-50"
          @click="confirmDeleteOpen = true"
        >
          {{ t('galleries.item.delete') }}
        </button>
        <div class="flex items-center gap-3">
          <button
            type="button"
            :disabled="saving"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            @click="handleClose"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            form="gallery-item-form"
            :disabled="saving"
            class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          >
            <AppSpinner v-if="saving" size="button" />
            {{ saving ? t('common.saving') : t('common.save') }}
          </button>
        </div>
      </div>
    </template>
  </SlideOver>

  <ConfirmDialog
    :open="confirmDeleteOpen"
    :title="t('galleries.confirm.delete_item_title')"
    :body="t('galleries.confirm.delete_item_body', { size: formatBytes(item?.bytes ?? 0) })"
    :confirm-label="t('galleries.item.delete')"
    @update:open="confirmDeleteOpen = $event"
    @confirm="confirmDelete"
  />
</template>
